import { vec3 } from 'gl-matrix';
import { enqueueSnackbar } from 'notistack';
import { useCallback, useState } from 'react';
import { RegionOfInterest } from '~/engine/data/structure/RegionOfInterest';
import { SDFOperations } from '~/engine/util/algorithms/sdf/SDFOperations';
import { Environment } from '~/engine/data/Environment';
import { getErrorMessage } from '~/utils/errors';
import { waitForUI } from '~/utils/uiUtils';
import { useGetOrCreateDestination } from '../shared/useGetOrCreateDestination';

interface UseMarginApplicationOptions {
    environment: Environment | null;
    inputRoi: RegionOfInterest | undefined;
    onSuccess: () => void;
    updateView: () => void;
}

export function useMarginApplication({
    environment,
    inputRoi,
    onSuccess,
    updateView,
}: UseMarginApplicationOptions) {
    const [isProcessing, setIsProcessing] = useState(false);
    const { getOrCreateDestination } = useGetOrCreateDestination({
        environment,
        inputRoi,
    });

    const handleSymmetricApply = useCallback(
        async (
            marginMm: number,
            outputRoiNumber: number | null,
            newName?: string
        ) => {
            if (!environment?.structureSet || !inputRoi?.texture) {
                return;
            }

            try {
                const dest = await getOrCreateDestination(
                    outputRoiNumber,
                    newName
                );
                if (!dest) {
                    return;
                }

                const marginTexture = await SDFOperations.applySymmetricMargin(
                    inputRoi.texture,
                    marginMm
                );
                if (!marginTexture) {
                    enqueueSnackbar('Failed to apply margin', {
                        variant: 'error',
                    });
                    return;
                }
                dest.replaceTexture(marginTexture, 'Symmetric Margin');
                updateView();
                enqueueSnackbar('Margin applied', { variant: 'success' });
                onSuccess();
            } catch (e) {
                console.error('Apply error:', e);
                enqueueSnackbar(
                    `Failed to apply margin: ${getErrorMessage(e)}`,
                    {
                        variant: 'error',
                    }
                );
            }
        },
        [environment, inputRoi, getOrCreateDestination, updateView, onSuccess]
    );

    const handleAsymmetricApply = useCallback(
        async (
            negLpsMm: vec3,
            posLpsMm: vec3,
            outputRoiNumber: number | null,
            newName?: string
        ) => {
            if (isProcessing) {
                enqueueSnackbar(
                    'Another margin operation is already in progress.',
                    { variant: 'warning' }
                );
                return;
            }

            if (!environment?.structureSet || !inputRoi?.texture) {
                return;
            }

            setIsProcessing(true);

            try {
                const dest = await getOrCreateDestination(
                    outputRoiNumber,
                    newName
                );
                if (!dest || !dest.texture) {
                    setIsProcessing(false);
                    return;
                }

                enqueueSnackbar('Applying margin...', { variant: 'info' });
                // TODO: improve kind of hacky rendering solutions should be removed and
                // heavy computation should be lifted away from blocking the main thread for smooth rendering.
                await waitForUI();
                try {
                    const modify = dest.startModify('Asymmetric Margin');
                    const result = await SDFOperations.applyAsymmetricMargin(
                        inputRoi.texture,
                        negLpsMm,
                        posLpsMm
                    );
                    dest.texture.resetSdfAndGrid(
                        result.signedDistanceField!.toArray(),
                        result.textureGrid
                    );
                    modify.finish();
                    updateView();
                    enqueueSnackbar(`Applied margin to "${dest.name}"`, {
                        variant: 'success',
                    });
                    onSuccess();
                } catch (e) {
                    console.error('Asymmetric apply error:', e);
                    enqueueSnackbar(
                        `Failed to apply margin: ${getErrorMessage(e)}`,
                        { variant: 'error' }
                    );
                } finally {
                    setIsProcessing(false);
                }
            } catch (e) {
                setIsProcessing(false);
                console.error('Asymmetric margin error:', e);
                enqueueSnackbar(
                    `Failed to apply margin: ${getErrorMessage(e)}`,
                    {
                        variant: 'error',
                    }
                );
            }
        },
        [
            environment,
            inputRoi,
            isProcessing,
            getOrCreateDestination,
            updateView,
            onSuccess,
        ]
    );

    return {
        isProcessing,
        handleSymmetricApply,
        handleAsymmetricApply,
    };
}
